/**
 * A two-segment ratio bar: the cache hit rate, with the ABSOLUTE hit and miss
 * counts beside it.
 *
 * IT TAKES THE PLAN, NOT `hit` AND `miss`. That is the Task 13 review's F5
 * closed by construction rather than by a test: with two interchangeable
 * numeric props, `<RatioBar hit={ratio.miss} miss={ratio.hit}>` is a one-token
 * mutation that made the bar say 90 % while the line beneath it said 1 hit /
 * 9 misses. With one pre-assembled `countsLine`, that swap has nowhere to live
 * -- it is now a mutation in lib/metrics.ts, where a Node test drives it.
 *
 * The counts are not decoration: 100 % over four events and 100 % over four
 * hundred thousand are the same bar and different facts, and the one an
 * operator would act on is the second. Same reason `offlineQueueDepth` renders
 * its `samples` column.
 *
 * `ratio === null` renders NO BAR AT ALL, only "unbekannt": a hit rate over
 * zero events is not 0 %, and a full-width bar over an idle hour would send
 * someone to look at a cache that is working fine.
 */
import { RATIO_HEIGHT, RATIO_WIDTH, ratioWidth } from '../../lib/chartScale';
import type { RatioPlan } from '../../lib/metrics';

export function RatioBar({ plan }: { plan: RatioPlan }) {
  const counts = <p className="chart-ratio-counts">{plan.countsLine}</p>;

  if (plan.ratio === null) {
    return (
      <div className="chart chart-ratio">
        <p className="chart-ratio-unknown">{plan.unknownLine}</p>
        {counts}
      </div>
    );
  }

  return (
    <div className="chart chart-ratio">
      <svg viewBox={`0 0 ${RATIO_WIDTH} ${RATIO_HEIGHT}`} role="img" aria-label={plan.ariaLabel}
        preserveAspectRatio="none">
        <title>{plan.ariaLabel}</title>
        <rect x="0" y="0" width={RATIO_WIDTH} height={RATIO_HEIGHT} fill="currentColor" fillOpacity="0.12" />
        {/* The HIT share. Drawing `1 - ratio` here makes the bar contradict the
            percentage printed directly beneath it — see lib/chartScale.ts. */}
        <rect x="0" y="0" width={ratioWidth(plan.ratio)} height={RATIO_HEIGHT} fill="var(--ok)" />
      </svg>
      <p className="chart-ratio-value">{plan.valueLine}</p>
      {counts}
    </div>
  );
}
